import { isShown, shownRef } from "@/components/GraphVisualisation/decisionGraphView";
import Select from "@imminently/decisively-ui/Select";
import React from "react";
import { useGraphVisualisation } from "../../../hooks/useGraphVisualisation";

interface DepthSelectorProps {}

const DepthSelector = (props: DepthSelectorProps) => {
  const {
    graph,
    visible,
    restrictViewToDepth,
    restrictedView,
  } = useGraphVisualisation();
  const restrictedViewRootPath = restrictedView?.rootPath;

  // The furthest any node around the focus is from it (undirected), looking up to 10 steps out each way
  const maxDepthFromRestrictedViewRootPath = React.useMemo(() => {
    const root = shownRef(graph, visible, restrictedViewRootPath);
    if (!graph || root === undefined || !graph.field(root, "description")) return null;

    const maxExploreDepth = 10;
    const include = (id: number) => isShown(visible, id);
    const relevant = new Set([
      root,
      ...graph.neighbourhood(root, maxExploreDepth, "predecessors", include),
      ...graph.neighbourhood(root, maxExploreDepth, "successors", include),
    ]);
    for (const id of relevant) if (!graph.field(id, "description")) relevant.delete(id);

    // breadth first over edges either way, within the relevant nodes
    const distance = new Map<number, number>([[root, 0]]);
    let frontier = [root];
    let maxDepthFromFocus = 0;
    while (frontier.length > 0) {
      const next: number[] = [];
      for (const id of frontier) {
        const d = distance.get(id)!;
        for (const neighbours of [graph.successors(id), graph.predecessors(id)]) {
          for (const to of neighbours) {
            if (!relevant.has(to) || distance.has(to)) continue;
            distance.set(to, d + 1);
            maxDepthFromFocus = Math.max(maxDepthFromFocus, d + 1);
            next.push(to);
          }
        }
      }
      frontier = next;
    }
    return maxDepthFromFocus;
  }, [graph, visible, restrictedViewRootPath]);

  React.useEffect(() => {
    const restrictedViewDepth = restrictedView?.graphDepth ?? null;
    if (
      restrictedViewDepth !== null &&
      maxDepthFromRestrictedViewRootPath !== null &&
      restrictedViewDepth > maxDepthFromRestrictedViewRootPath
    ) {
      restrictViewToDepth(maxDepthFromRestrictedViewRootPath);
    } else if (maxDepthFromRestrictedViewRootPath === null && restrictedViewDepth !== null) {
      restrictViewToDepth(null);
    } else if (restrictedViewDepth === null && maxDepthFromRestrictedViewRootPath !== null) {
      if (maxDepthFromRestrictedViewRootPath > 0) {
        restrictViewToDepth(1);
      } else if (maxDepthFromRestrictedViewRootPath === 0) {
        restrictViewToDepth(0);
      }
    }
  }, [maxDepthFromRestrictedViewRootPath, restrictedView?.graphDepth, restrictViewToDepth]);

  const options = React.useMemo(() => {
    const opts = maxDepthFromRestrictedViewRootPath
      ? Array.from(Array(maxDepthFromRestrictedViewRootPath).keys()).map((i) => ({
          value: (++i).toString(),
          label: i.toString(),
        }))
      : [];
    opts.unshift({ value: "all", label: "All" });
    return opts;
  }, [maxDepthFromRestrictedViewRootPath]);

  const isDisabled =
    !(maxDepthFromRestrictedViewRootPath !== null && maxDepthFromRestrictedViewRootPath >= 0) ||
    maxDepthFromRestrictedViewRootPath === 0;

  const currentValue = React.useMemo(() => {
    const restrictedViewDepth = restrictedView?.graphDepth;
    if (!restrictedViewDepth) {
      return "all";
    }
    return restrictedViewDepth.toString();
  }, [restrictedView?.graphDepth]);

  const handleChange = React.useCallback(
    (value: string | null) => {
      if (value === "all") {
        restrictViewToDepth(null);
      } else {
        restrictViewToDepth(Number(value));
      }
    },
    [restrictViewToDepth],
  );

  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="whitespace-nowrap text-muted-foreground">Max depth</span>
      <Select
        value={currentValue}
        options={options}
        onValueChange={handleChange}
        disabled={isDisabled}
        className="min-w-[8rem]"
      />
    </label>
  );
};

export default DepthSelector;
